import { compassLayout, type CompassMarker, FIELD_DEG, TAPE_FROM, TAPE_TO, tapeLeftPercent } from "../world/compass";

const CARDINAL = ["北", "東", "南", "西"];
const ICON: Record<CompassMarker["kind"], string> = {
  estate: `<svg viewBox="0 0 16 16"><path d="M8 2 1 8h2v6h4v-4h2v4h4V8h2z"/></svg>`,
  library: `<svg viewBox="0 0 16 16"><path d="M2 3h5c.4 0 .7.1 1 .3.3-.2.6-.3 1-.3h5v10H9c-.6 0-1 .4-1 1 0-.6-.4-1-1-1H2z"/></svg>`,
  mark: `<svg viewBox="0 0 16 16"><path d="M8 1a5 5 0 0 0-5 5c0 4 5 9 5 9s5-5 5-9a5 5 0 0 0-5-5zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/></svg>`,
};

export interface CompassHUD {
  setMarkers(m: readonly CompassMarker[]): void;
  update(yawDeg: number, x: number, z: number): void;
}

/** The FPS-style compass above the hotbar (spec 2e §2.1): a tick tape that slides with the yaw, and markers. */
export function mountCompass(el: HTMLElement): CompassHUD {
  el.innerHTML = `<div class="compass-tape"></div><div class="compass-needle"></div><div class="compass-marks"></div>`;
  const tape = el.querySelector<HTMLElement>(".compass-tape")!;
  const marksEl = el.querySelector<HTMLElement>(".compass-marks")!;
  const span = TAPE_TO - TAPE_FROM;
  tape.style.width = `${(span / (2 * FIELD_DEG)) * 100}%`;
  for (let d = TAPE_FROM; d < TAPE_TO; d += 15) {
    const deg = ((d % 360) + 360) % 360;
    const tick = document.createElement("div");
    tick.className = deg % 90 === 0 ? "tick cardinal" : deg % 45 === 0 ? "tick major" : "tick";
    tick.style.left = `${((d - TAPE_FROM) / span) * 100}%`;
    if (deg % 90 === 0) tick.textContent = CARDINAL[deg / 90]!;
    tape.appendChild(tick);
  }
  let markers: readonly CompassMarker[] = [];
  const nodes = new Map<string, { el: HTMLElement; label: HTMLElement; left: HTMLElement; right: HTMLElement }>();
  return {
    setMarkers(m) {
      markers = m;
      marksEl.textContent = "";
      nodes.clear();
      // later children paint on top: reverse so the estate sits above the library and the marks
      for (const mk of [...m].reverse()) {
        const e = document.createElement("div");
        e.className = `compass-mark ${mk.kind}`;
        // the arrows sit beside the icon inside the marker: the compass clips anything outside it
        e.innerHTML = `<span class="icon-row"><span class="arrow left"></span>${ICON[mk.kind]}<span class="arrow right"></span></span><span class="label"></span>`;
        marksEl.appendChild(e);
        nodes.set(mk.id, {
          el: e, label: e.querySelector<HTMLElement>(".label")!,
          left: e.querySelector<HTMLElement>(".arrow.left")!, right: e.querySelector<HTMLElement>(".arrow.right")!,
        });
      }
    },
    update(yawDeg, x, z) {
      tape.style.left = `${tapeLeftPercent(yawDeg)}%`;
      for (const p of compassLayout(yawDeg, { x, z }, markers)) {
        const n = nodes.get(p.id);
        if (!n) continue;
        n.el.style.left = `${50 + p.t * 50}%`;
        const l = p.clamped && p.t < 0, r = p.clamped && p.t > 0;
        n.el.classList.toggle("clamped-left", l);
        n.el.classList.toggle("clamped-right", r);
        n.left.textContent = l ? "‹" : "";
        n.right.textContent = r ? "›" : "";
        if (n.label.textContent !== p.label) n.label.textContent = p.label;
      }
    },
  };
}
